import { V2_ENDPOINT } from "@/consts";
import { authenticatedFetch } from "./authenticatedFetch";

export interface PushoverRecipient {
  id: string;
  organizationId: string;
  connectorId: string;
  connectorName?: string;
  userKey: string;
  label?: string;
  device?: string;
  verifiedAt: string;
}

export interface PaginatedResponse<T> {
  items: T[];
  limit: number;
  offset: number;
  hasMore: boolean;
}

interface ApiResponse<T> {
  code: number;
  message: string;
  data: T;
  requestId: string;
}

async function unwrap<T>(response: Response): Promise<T> {
  if (!response.ok) {
    const errorData = await response.json().catch(() => ({ message: response.statusText }));
    throw new Error(errorData.message || response.statusText);
  }
  const data = (await response.json()) as ApiResponse<T>;
  if (data.code !== 0) {
    throw new Error(`API error: ${data.message}`);
  }
  return data.data;
}

export async function listPushoverRecipients(
  connectorId?: string,
  limit = 50,
  offset = 0,
): Promise<PaginatedResponse<PushoverRecipient>> {
  const params = new URLSearchParams();
  params.set("limit", String(limit));
  params.set("offset", String(offset));
  if (connectorId) params.set("connector_id", connectorId);

  const response = await authenticatedFetch<ApiResponse<PaginatedResponse<PushoverRecipient>>>(
    `${V2_ENDPOINT}/v1/pushover/recipients?${params.toString()}`,
  );
  return unwrap<PaginatedResponse<PushoverRecipient>>(response);
}

export async function addPushoverRecipient(input: {
  connectorId: string;
  userKey: string;
  label?: string;
  device?: string;
}): Promise<{ recipient: PushoverRecipient }> {
  const response = await authenticatedFetch<ApiResponse<{ recipient: PushoverRecipient }>>(
    `${V2_ENDPOINT}/v1/pushover/recipients`,
    {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(input),
    },
  );
  return unwrap<{ recipient: PushoverRecipient }>(response);
}

export async function deletePushoverRecipient(id: string): Promise<void> {
  const response = await authenticatedFetch(`${V2_ENDPOINT}/v1/pushover/recipients/${id}`, {
    method: "DELETE",
  });
  if (!response.ok) {
    const errorData = await response.json().catch(() => ({ message: response.statusText }));
    throw new Error(errorData.message || response.statusText);
  }
}
